import { useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { apiV2 } from "../../../api/axiosConfig";
import { useMapSettings } from "../../operational-map/context/useMapSettings";
import type { Overlay, Feature } from "../../../types/overlay";

// --- Fetch ---

async function fetchOverlays(): Promise<Overlay[]> {
    const res = await apiV2.get("ops/overlays");
    return res.data ?? [];
}

// --- Types ---

export type OverlayFeatureClickData = {
    name: string;
    description: string;
    position: { x: number; y: number };
};

// --- Shape management ---

type ShapeHandle = google.maps.Circle | google.maps.Polygon | google.maps.Polyline | google.maps.Marker;

type OverlayShapes = {
    overlayID: string;
    shapes: ShapeHandle[];
};

function clearOverlayShapes(entries: OverlayShapes[]) {
    for (const entry of entries) {
        entry.shapes.forEach((s) => s.setMap(null));
    }
    entries.length = 0;
}

function hasPopupContent(feature: Feature): boolean {
    return !!(feature.properties.name || feature.properties.description);
}

function clientCoordsFromMapEvent(e: google.maps.MapMouseEvent): { x: number; y: number } {
    const de = e.domEvent as MouseEvent | TouchEvent | undefined;
    if (!de) return { x: 0, y: 0 };
    if ("clientX" in de) return { x: de.clientX, y: de.clientY };
    const touch = (de as TouchEvent).changedTouches?.[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : { x: 0, y: 0 };
}

function renderFeature(
    feature: Feature,
    map: google.maps.Map,
    onClickFeature: (data: OverlayFeatureClickData) => void,
    forceCursor: (cursor: string) => void,
): ShapeHandle[] {
    const shapes: ShapeHandle[] = [];
    const s = feature.properties.style;
    const geo = feature.geometry;
    const clickable = hasPopupContent(feature);

    const fireClick = (e: google.maps.MapMouseEvent) => {
        onClickFeature({
            name: feature.properties.name ?? "",
            description: feature.properties.description ?? "",
            position: clientCoordsFromMapEvent(e),
        });
    };

    if (geo.type === "Polygon") {
        const path = geo.coordinates.map(([lng, lat]) => ({ lat, lng }));
        const polygon = new google.maps.Polygon({
            map,
            paths: path,
            strokeColor: s.lineColor,
            strokeOpacity: s.lineOpacity,
            strokeWeight: s.lineWidth,
            fillColor: s.fillColor,
            fillOpacity: s.fillOpacity,
            zIndex: 50,
        });
        if (clickable) {
            polygon.addListener("mouseover", () => forceCursor("pointer"));
            polygon.addListener("mouseout",  () => forceCursor("grab"));
            polygon.addListener("click", fireClick);
        }
        shapes.push(polygon);

    } else if (geo.type === "Point") {
        const [lng, lat] = geo.coordinates;
        const center = { lat, lng };

        if (feature.properties.radius) {
            const circle = new google.maps.Circle({
                map,
                center,
                radius: feature.properties.radius,
                strokeColor: s.lineColor,
                strokeOpacity: s.lineOpacity,
                strokeWeight: s.lineWidth,
                fillColor: s.fillColor,
                fillOpacity: s.fillOpacity,
                zIndex: 50,
            });
            if (clickable) {
                circle.addListener("mouseover", () => forceCursor("pointer"));
                circle.addListener("mouseout",  () => forceCursor("grab"));
                circle.addListener("click", fireClick);
            }
            shapes.push(circle);
        } else {
            const marker = new google.maps.Marker({
                map,
                position: center,
                zIndex: 100,
                ...(s.fontSize
                    ? {
                        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 0 },
                        label: { text: feature.properties.label ?? "", color: s.fontColor ?? "#000", fontSize: `${s.fontSize}px` },
                    }
                    : {
                        label: {
                            text: feature.properties.icon ?? "\ue55e",
                            fontFamily: "Material Icons",
                            color: "#ffffff",
                            fontSize: "18px",
                        },
                    }),
            });
            if (clickable) {
                marker.addListener("click", fireClick);
                // Markers already show pointer cursor by default
            }
            shapes.push(marker);
        }

    } else if (geo.type === "LineString") {
        const path = geo.coordinates.map(([lng, lat]) => ({ lat, lng }));
        const polyline = new google.maps.Polyline({
            map,
            path,
            strokeColor: s.lineColor,
            strokeOpacity: s.lineOpacity,
            strokeWeight: s.lineWidth,
            zIndex: 75,
        });
        if (clickable) {
            polyline.addListener("mouseover", () => forceCursor("pointer"));
            polyline.addListener("mouseout",  () => forceCursor("grab"));
            polyline.addListener("click", fireClick);
        }
        shapes.push(polyline);
    }

    return shapes;
}

// --- Bounds calculation ---

function overlayBounds(overlay: Overlay): google.maps.LatLngBounds | null {
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    for (const feature of overlay.data?.features ?? []) {
        const geo = feature.geometry;
        if (geo.type === "Point") {
            const [lng, lat] = geo.coordinates;
            if (feature.properties.radius) {
                // Compute circle extents using spherical math — avoids relying on
                // google.maps.Circle.getBounds() which needs a map projection to be ready.
                const r = feature.properties.radius;
                const latDelta = r / 111320;
                const lngDelta = r / (111320 * Math.cos((lat * Math.PI) / 180));
                bounds.extend({ lat: lat + latDelta, lng: lng + lngDelta });
                bounds.extend({ lat: lat - latDelta, lng: lng - lngDelta });
            } else {
                bounds.extend({ lat, lng });
            }
            hasPoints = true;
        } else if (geo.type === "Polygon" || geo.type === "LineString") {
            for (const [lng, lat] of geo.coordinates) {
                bounds.extend({ lat, lng });
                hasPoints = true;
            }
        }
    }

    return hasPoints ? bounds : null;
}

// --- Hook ---

export function useOverlaysLayer(
    map: google.maps.Map | null,
    onClickFeature: (data: OverlayFeatureClickData) => void,
    forceCursor: (cursor: string) => void,
) {
    const { settings } = useMapSettings();
    const { selectedOverlayIDs } = settings;

    const renderedRef      = useRef<OverlayShapes[]>([]);
    const allOverlaysRef   = useRef<Overlay[]>([]);
    const mapRef           = useRef(map);
    const onClickRef       = useRef(onClickFeature);
    const forceCursorRef   = useRef(forceCursor);
    useEffect(() => { mapRef.current = map; }, [map]);
    onClickRef.current     = onClickFeature;
    forceCursorRef.current = forceCursor;

    const { data: overlays } = useQuery(
        ["ops-overlays"],
        fetchOverlays,
        { staleTime: 5 * 60_000 },
    );

    // Keep in sync synchronously (not via useEffect) so fitOverlay always has current data
    allOverlaysRef.current = overlays ?? [];

    useEffect(() => {
        if (!map) return;

        clearOverlayShapes(renderedRef.current);

        const toRender = allOverlaysRef.current.filter((o) => selectedOverlayIDs.includes(o.id));

        for (const overlay of toRender) {
            const shapes: ShapeHandle[] = [];
            for (const feature of overlay.data?.features ?? []) {
                shapes.push(...renderFeature(
                    feature,
                    map,
                    (d) => onClickRef.current(d),
                    (c) => forceCursorRef.current(c),
                ));
            }
            renderedRef.current.push({ overlayID: overlay.id, shapes });
        }

        return () => {
            clearOverlayShapes(renderedRef.current);
        };
    }, [map, selectedOverlayIDs, overlays]);

    function fitOverlay(id: string) {
        const m = mapRef.current;
        if (!m) return;
        const overlay = allOverlaysRef.current.find((o) => o.id === id);
        if (!overlay) return;
        const bounds = overlayBounds(overlay);
        if (bounds) m.fitBounds(bounds, 48);
    }

    return { fitOverlay };
}

export type { Overlay };
